<script setup lang="ts">
import GaiaIconButton from '../base/GaiaIconButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** e.g. "3 selected" or "Merge mode". */
  label: {
    type: String,
    required: true,
  },
  /** Muted explainer after the divider, e.g. "Pick the two records to combine." */
  hint: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{ clear: [] }>();

const border = gaiaNeutral.border;
const surface = gaiaNeutral.surfaceSoft;
const divider = gaiaNeutral.borderMuted;
const clearColor = gaiaNeutral.textTertiary;
const clearHover = gaiaNeutral.surfaceHoverStrong;
</script>

<template>
  <!-- Bulk-selection toolbar from the CM list mock: dismiss, count, divider,
       then the page's bulk actions (default slot — round GaiaIconButtons).
       Bespoke chrome: the fixed 42px height and hairline frame are not naive
       Card/Alert vars.

       Two deliberate choices here. The bar is quiet — a soft surface and a
       plain hairline rather than an ink frame and halo — because it appears
       every time a row is ticked and shouldn't shout over the table it sits on.
       And dismiss leads rather than trails: it is the way out of a mode, so it
       belongs where the eye starts, not stranded past a row of actions whose
       length changes with the page. -->
  <n-flex align="center" :size="10" :wrap="false" class="gaia-selection-bar">
    <gaia-icon-button
      :dimension="26"
      quaternary
      aria-label="Clear selection"
      title="Clear selection"
      class="clear"
      @click="emit('clear')"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
      >
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </gaia-icon-button>

    <gaia-text size="body" weight="semibold" tone="ink">{{ props.label }}</gaia-text>
    <span class="divider" />
    <gaia-text v-if="props.hint" size="dense" tone="tertiary">{{ props.hint }}</gaia-text>
    <n-flex align="center" :size="8" :wrap="false"><slot /></n-flex>
  </n-flex>
</template>

<style scoped>
.gaia-selection-bar {
  height: 42px;
  padding: 0 12px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
}

.clear {
  flex: none;
  color: v-bind(clearColor);
}

.clear:hover {
  background: v-bind(clearHover);
}

/* n-divider carries its own margins and a min-height that fights a 42px bar. */
.divider {
  flex: none;
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background: v-bind(divider);
}
</style>
